import { useEffect, useState } from "react";
import { Modal, Button, Row, Col } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import "../utils/AddDocVerify.css"
import { addDocKycMerchand } from "../services/merchandApi";
import { deleteMedia } from "../../media/services/mediaApi";
import { useQuery, useQueryClient } from "react-query"
import { Tabs } from "antd";
import { IdcardOutlined, ShopOutlined } from "@ant-design/icons";
import MerchantIdentityTab from "./doc/MerchantIdentityTab";
import AddFirstBusiness from "./doc/FirstBusiness";
import { getPaymentMethods } from "../../transactions/services/transactionApi";
import RecapNewMerchant from "./doc/RecapNewMerchant";
import loadingAnim from "../../../assets/animations/loading.json";
import Lottie from "lottie-react";
import { cleanupAll } from "./cleanAll";


const AddDocVerify = ({ modal, onUpdateMerchantKyc, currentUser, toggle }) => {
    
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState("1");
    const [isStep1Valid, setIsStep1Valid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [createdBusiness, setCreatedBusiness] = useState(null);
    

    const typeList = [
        { id: "CNI", label: "Carte Nationale d'identité" },
        { id: "PASSPORT", label: "Passport" },
    ];
    const { data, isLoading: isLoadingPaym, isError: isErrorPaym, error: errorPaym } = useQuery(
      "paymentList", getPaymentMethods
    );
    const paymentList = Array.isArray(data?.data) ? data.data : [];  

    useEffect(() => {
        if (modal) {
            formik.resetForm();
            setCreatedBusiness(null);
            setIsStep1Valid(false);
            setActiveTab("1");
        }
    }, [modal]);

    const validationSchema = Yup.object({
        type: Yup.string().required("Veuillez sélectionner un type de document"),
        merchantRecto: Yup.mixed().required("Veuillez uploader le document"),
        RIB: Yup.mixed().required("Veuillez uploader le RIB"),
        Domicile: Yup.mixed().required("Veuillez uploader le domicile"),
    });

    const initialValues= {
      type: "",
      merchantRecto: null,
      merchantVerso: null,
      RIB: null,
      Domicile: null,
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values) => { 
            if (!values.merchantRecto || (values.type === "CNI" && !values.merchantVerso) || !values.RIB || !values.Domicile) {
                return toast.error("Veuillez uploader tous les documents requis.");
            }
            if (!createdBusiness) {
                return toast.error("Erreur, Merci de contacter le support");
            }
            setIsSubmitting(true);
            try {
                const files = {
                    token: currentUser?.token,
                    merchandId: currentUser?.user?.id || currentUser?.userId,
                    
                    businessId: createdBusiness?.id, 
                    merchantRecto: values.merchantRecto,
                    merchantVerso: values.type === "CNI" ? values.merchantVerso : null,
                    merchantDocumentType: values.type,
                    RIB: values.RIB,
                    Domicile: values.Domicile,
                    
                };
                const resp = await addDocKycMerchand(files);
                if(resp){
                    onUpdateMerchantKyc({
                        ...currentUser,
                        kycStatus:resp?.kycStatus,                    
                        kycVerified:resp?.kycVerified,                    
                    });
                }
                setError(null);
                setActiveTab("1");
                await queryClient.invalidateQueries("auth");
                toast.success("Documents KYC soumis avec succès !");
                formik.resetForm();
                setIsSubmitting(false);
                toggle();
            } catch (err) {
                setIsSubmitting(false);
                console.error(err);
                setError(err?.response?.data?.message || "Contactez le support"); 
                try {
                    if (values.merchantRecto === "string") {
                        await deleteMedia(values.merchantRecto);
                    }
                    if (typeof values.merchantVerso === "string") {
                        await deleteMedia(values.merchantVerso);
                    }
                    if (typeof values.RIB === "string") {
                        await deleteMedia(values.RIB);
                    }
                    if (values.Domicile === "string") {
                        await deleteMedia(values.Domicile);
                    }
                } catch (deleteErr) {
                    console.error("Erreur suppression : ", deleteErr);
                }
                toast.error("Erreur lors de la soumission, réessayez.");
            }
        },
    });

    const handleFinalSubmit = () => {
        formik.submitForm();
    };
    
    const handleCancel = () => {
        if (isSubmitting) {
            toast("Soumission en cours...");
            return;
        }

        Modal.confirm({
            title: "Annuler la soumission KYC ?",
            content: "Toutes les données seront supprimées.",
            okText: "Oui",
            cancelText: "Non",
            onOk: async () => {
            try {
                setIsSubmitting(true);

                await cleanupAll({
                merchantDocs: formik.values,
                business: createdBusiness,
                businessDocs: null,
                });

                toast.success("Opération annulée");
            } catch (err) {
                console.error(err);
                toast.error("Erreur lors de l'annulation");
            } finally {
                setIsSubmitting(false);
                toggle();
            }
            },
        });
    };

    const tabItems = [
        {
            key: "1",
            label: (
                <>
                    <IdcardOutlined /> Identifiant marchand
                </>
            ),
            children: (
                <MerchantIdentityTab
                    formik={formik}
                    typeList={typeList}
                    isSubmitting={isSubmitting}
                    error={error}
                    toggle={toggle}
                    onNext={() => {
                        setCreatedBusiness(null);
                        setIsStep1Valid(true);
                        setActiveTab("2");
                    }}
                />
            ),
        },
        {
            key: "2",
            label: (
                <>
                    <ShopOutlined /> Mon premier business
                </>
            ),
            disabled: !isStep1Valid,
            children: (
                <div style={{ padding: 20 }}>
                    <AddFirstBusiness
                        toggle={toggle}
                        currentuser={currentUser}
                        paymentList={paymentList}
                        isLoadingPaym = {isLoadingPaym}
                        isErrorPaym = {isErrorPaym}
                        errorPaym = {errorPaym}
                        onBusinessCreated={(data) => {
                            setCreatedBusiness(data);
                            setActiveTab("3");
                        }}
                    />
                </div>
            ),
        },
        {
            key: "3",
            label: "Récapitulatif",
            disabled: !createdBusiness,
            children: (
                <RecapNewMerchant
                    merchantDocs={formik.values}
                    createdBusiness={createdBusiness}
                    onSubmitFinal={handleFinalSubmit}
                    isSubmitting={isSubmitting}
                    error={error}
                    onCancelAll={handleCancel}
                />
            ),
        }
    ];


    
    return (
        <Modal
            title="Vérification d'identité marchand"
            open={modal}
            onCancel={handleCancel}
            footer={null}
            centered
            width={1000}
            maskClosable={false}
            keyboard={!isSubmitting}
        >
            <Tabs
                centered
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
                items={tabItems}
            />
            {isSubmitting && (
                <div className="fc-loading-overlay">
                    <Lottie
                        animationData={loadingAnim}
                        loop
                        style={{ width: 150, height: 150 }}
                    />
                </div>
            )}
        </Modal>
    );
};

export default AddDocVerify;